import { useEffect, useRef, useState } from 'react';
import {
  Check,
  Copy,
  Globe,
  Loader2,
  Mail,
  Megaphone,
  MessageSquare,
  RefreshCw,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { useGeneratePromo } from '@/hooks/activation';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import {
  ActivationPackWithTarget,
  PromoTemplateType,
  PromoTone,
  PROMO_TEMPLATE_OPTIONS,
  PROMO_TONE_OPTIONS,
  DEFAULT_PROMO_TONES,
} from '@/types/activation/activation-pack';

interface PackPromoteSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

const TEMPLATE_ICONS: Record<PromoTemplateType, typeof Mail> = {
  launch_email: Mail,
  social_post: Megaphone,
  customer_sms: MessageSquare,
  team_announcement: Users,
  website_banner: Globe,
};

export const PackPromoteSection = ({ pack, brand = '#4E9CFF' }: PackPromoteSectionProps) => {
  const targetUrl = ActivationPackService.getTargetUrl(pack);
  const { mutate: generatePromo } = useGeneratePromo();

  const [tones, setTones] = useState<Record<PromoTemplateType, PromoTone>>(DEFAULT_PROMO_TONES);
  const [pendingVariants, setPendingVariants] = useState<Set<string>>(new Set());
  const [copiedTemplate, setCopiedTemplate] = useState<PromoTemplateType | null>(null);
  const autoFiredRef = useRef<Set<string>>(new Set());

  const variantKey = (template: PromoTemplateType, tone: PromoTone) => `${template}:${tone}`;

  const fireGeneration = (template: PromoTemplateType, tone: PromoTone) => {
    const key = variantKey(template, tone);
    if (pendingVariants.has(key)) return;

    setPendingVariants((prev) => new Set(prev).add(key));
    generatePromo(
      { packId: pack.id, template, tone, targetUrl },
      {
        onSettled: () => {
          setPendingVariants((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        },
      },
    );
  };

  // Auto-generate any missing default-tone variants on first load. Fires once
  // per (template, tone) per session.
  useEffect(() => {
    PROMO_TEMPLATE_OPTIONS.forEach((option) => {
      const tone = DEFAULT_PROMO_TONES[option.value];
      const key = variantKey(option.value, tone);
      const cached = pack.promo_content?.[option.value]?.[tone];

      if (!cached && !autoFiredRef.current.has(key)) {
        autoFiredRef.current.add(key);
        fireGeneration(option.value, tone);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.id, pack.promo_content]);

  const handleToneChange = (template: PromoTemplateType, tone: PromoTone) => {
    setTones((prev) => ({ ...prev, [template]: tone }));
    const cached = pack.promo_content?.[template]?.[tone];
    const key = variantKey(template, tone);
    if (!cached && !autoFiredRef.current.has(key)) {
      autoFiredRef.current.add(key);
      fireGeneration(template, tone);
    }
  };

  const handleCopy = async (template: PromoTemplateType, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTemplate(template);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <OverviewSectionCard
      title="Promote"
      subtitle="Ready-to-paste launch copy — generated automatically from your Genie's name, description, and link"
      genieIcon="share"
      brand={brand}
    >
      <div className="space-y-4">
        {PROMO_TEMPLATE_OPTIONS.map((option) => {
          const selectedTone = tones[option.value];
          const cached = pack.promo_content?.[option.value]?.[selectedTone];
          const isLoading = pendingVariants.has(variantKey(option.value, selectedTone));
          const isCopied = copiedTemplate === option.value;
          const Icon = TEMPLATE_ICONS[option.value];

          const fullText = cached
            ? cached.subject
              ? `Subject: ${cached.subject}\n\n${cached.body}`
              : cached.body
            : '';

          return (
            <div
              key={option.value}
              className="rounded-xl border border-gray-200 bg-white overflow-hidden"
            >
              {/* Card header */}
              <div
                className="px-4 md:px-5 py-3 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                style={{ background: `${brand}05`, borderColor: `${brand}15` }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${brand}15` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: brand }} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">
                      {option.label}
                    </div>
                    <div className="text-xs text-gray-600 truncate">{option.description}</div>
                  </div>
                </div>

                {/* Tone selector */}
                <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-0.5 shrink-0">
                  {PROMO_TONE_OPTIONS.map((toneOption) => {
                    const isActive = selectedTone === toneOption.value;
                    return (
                      <button
                        key={toneOption.value}
                        onClick={() => handleToneChange(option.value, toneOption.value)}
                        className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                        style={
                          isActive
                            ? { backgroundColor: brand, color: 'white' }
                            : { color: '#64748B' }
                        }
                      >
                        {toneOption.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Card body */}
              <div className="px-4 md:px-5 py-4">
                {cached ? (
                  <>
                    {cached.subject && (
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Subject
                        </div>
                        <div className="text-sm font-medium text-gray-900">{cached.subject}</div>
                      </div>
                    )}
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Body
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed mb-4">
                      {cached.body}
                    </pre>

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Generated {new Date(cached.generated_at).toLocaleString()}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fireGeneration(option.value, selectedTone)}
                          disabled={isLoading}
                          className="h-8 text-gray-600"
                          title="Generate a fresh version"
                        >
                          {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleCopy(option.value, fullText)}
                          className="h-8 text-white border-0"
                          style={{
                            background: `linear-gradient(90deg, ${brand} 0%, #FF4DB8 100%)`,
                          }}
                        >
                          {isCopied ? (
                            <>
                              <Check className="w-4 h-4 mr-1.5" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1.5" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: brand }} />
                    <span className="text-sm text-gray-600">
                      Drafting your {option.label.toLowerCase()}…
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </OverviewSectionCard>
  );
};
